'use client';

import React, { useState, useCallback } from 'react';

// ─── MANA NEWSLETTER EMBEDDABLE COMPONENTS ────────────────────────────────────
// Each component is individually exportable and embeddable via:
//   1. Standalone React component (import directly)
//   2. Embed code (iframe or script tag)
//   3. JSON data export for external use
//
// Usage: <MANAArticleCard article={...} />
//        <MANACategoryBadge category="marriage-family" />
//        <MANANewsletterIssue issue={...} />
//        <MANAEmbedGenerator componentType="article-card" data={...} />

// ─── TYPES ────────────────────────────────────────────────────────────────────
export interface MANAArticle {
  id: string;
  source_name: string;
  source_domain: string;
  article_title: string;
  article_url: string;
  article_summary: string;
  interest_category: string;
  relevance_score: number;
  relevance_reason?: string;
  tags?: string[];
  status: string;
  fetched_at: string;
}

export interface MANANewsletterIssueData {
  id: string;
  issue_number: number;
  title: string;
  published_at: string;
  articles: MANAArticle[];
  categories: string[];
}

export const CATEGORY_CONFIG: Record<string, { label: string; color: string; emoji: string }> = {
  'marriage-family': { label: 'Marriage & Family', color: '#c47a7a', emoji: '🏡' },
  'history-heritage': { label: 'History & Heritage', color: '#c9a84c', emoji: '📜' },
  'legacy-profiles': { label: 'Legacy & Profiles', color: '#8a8ab8', emoji: '✦' },
  'youth-young-adults': { label: 'Youth & Young Adults', color: '#2d8a87', emoji: '🌱' },
  'faith-ramadan': { label: 'Faith & Ramadan', color: '#D4A82A', emoji: '☽' },
  'justice-civic': { label: 'Justice & Civic Life', color: '#2d7a47', emoji: '⚖' },
};

// ─── EMBED BASE URL ───────────────────────────────────────────────────────────
const EMBED_BASE_URL = 'https://haalprojec7875.builtwithrocket.new';

// ─── COPY TO CLIPBOARD HOOK ───────────────────────────────────────────────────
function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);
  return { copied, copy };
}

// ─── 1. MANA CATEGORY BADGE ───────────────────────────────────────────────────
// Embeddable: <MANACategoryBadge category="marriage-family" />
export function MANACategoryBadge({
  category,
  size = 'md',
  showEmoji = true,
}: {
  category: string;
  size?: 'sm' | 'md' | 'lg';
  showEmoji?: boolean;
}) {
  const cfg = CATEGORY_CONFIG[category] || { label: category, color: '#c9a84c', emoji: '◈' };
  const fontSize = size === 'sm' ? 7 : size === 'lg' ? 11 : 9;
  const padding = size === 'sm' ? '2px 6px' : size === 'lg' ? '5px 12px' : '3px 8px';

  return (
    <span
      data-mana-component="category-badge"
      data-category={category}
      style={{
        fontFamily: 'monospace',
        fontSize,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: cfg.color,
        padding,
        border: `1px solid ${cfg.color}30`,
        borderRadius: 3,
        background: `${cfg.color}10`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
      }}
    >
      {showEmoji && <span>{cfg.emoji}</span>}
      {cfg.label}
    </span>
  );
}

// ─── 2. MANA ARTICLE CARD ─────────────────────────────────────────────────────
// Embeddable: <MANAArticleCard article={...} />
export function MANAArticleCard({
  article,
  variant = 'default',
  showBridgeLinks = true,
}: {
  article: MANAArticle;
  variant?: 'default' | 'compact' | 'featured';
  showBridgeLinks?: boolean;
}) {
  const cfg = CATEGORY_CONFIG[article.interest_category] || { label: article.interest_category, color: '#c9a84c', emoji: '◈' };
  const isFeatured = variant === 'featured';
  const isCompact = variant === 'compact';

  return (
    <article
      data-mana-component="article-card"
      data-article-id={article.id}
      data-category={article.interest_category}
      style={{
        padding: isFeatured ? '24px 28px' : isCompact ? '10px 14px' : '16px 20px',
        background: isFeatured ? `${cfg.color}06` : '#0a1018',
        border: `1px solid ${cfg.color}${isFeatured ? '25' : '15'}`,
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: 6,
        fontFamily: "'Georgia', serif",
      }}
    >
      {/* Category + Source */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: isCompact ? 4 : 8, flexWrap: 'wrap' }}>
        <MANACategoryBadge category={article.interest_category} size={isCompact ? 'sm' : 'md'} />
        <span style={{
          fontFamily: 'monospace',
          fontSize: 7,
          color: 'rgba(212,197,169,0.35)',
          letterSpacing: '.06em',
        }}>
          {article.source_name}
          {article.relevance_score && ` · ${article.relevance_score}% match`}
        </span>
      </div>

      {/* Title */}
      <a
        href={article.article_url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontFamily: "'Georgia', serif",
          fontSize: isFeatured ? 18 : isCompact ? 13 : 15,
          color: '#F5F0E8',
          textDecoration: 'none',
          lineHeight: 1.4,
          display: 'block',
          marginBottom: isCompact ? 0 : 8,
          fontWeight: isFeatured ? 400 : 400,
        }}
      >
        {article.article_title}
      </a>

      {/* Summary */}
      {!isCompact && article.article_summary && (
        <p style={{
          fontFamily: "'Georgia', serif",
          fontSize: 13,
          color: 'rgba(212,197,169,0.6)',
          lineHeight: 1.7,
          margin: '0 0 8px',
        }}>
          {article.article_summary}
        </p>
      )}

      {/* Tags */}
      {!isCompact && article.tags && article.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: showBridgeLinks ? 8 : 0 }}>
          {article.tags.slice(0, 4).map((tag) => (
            <span key={tag} style={{
              fontFamily: 'monospace',
              fontSize: 6,
              color: 'rgba(212,197,169,0.3)',
              padding: '1px 5px',
              border: '1px solid rgba(212,197,169,0.1)',
              borderRadius: 2,
            }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Bridge Links */}
      {showBridgeLinks && !isCompact && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <a href="/inner-reality-lens" style={{ fontFamily: 'monospace', fontSize: 7, color: '#8a8ab8', textDecoration: 'none', letterSpacing: '.06em' }}>
            ◎ IRL Lens
          </a>
          <a href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', textDecoration: 'none', letterSpacing: '.06em' }}>
            ⬡ MRG
          </a>
          <a href="/open-letter" style={{ fontFamily: 'monospace', fontSize: 7, color: '#c9a84c', textDecoration: 'none', letterSpacing: '.06em' }}>
            ✦ Open Letter
          </a>
        </div>
      )}
    </article>
  );
}

// ─── 3. MANA CATEGORY SECTION ─────────────────────────────────────────────────
// Embeddable: <MANACategorySection category="marriage-family" articles={[...]} />
export function MANACategorySection({
  category,
  articles,
  maxArticles = 5,
  showHeader = true,
}: {
  category: string;
  articles: MANAArticle[];
  maxArticles?: number;
  showHeader?: boolean;
}) {
  const cfg = CATEGORY_CONFIG[category] || { label: category, color: '#c9a84c', emoji: '◈' };
  const filtered = articles.filter(a => a.interest_category === category).slice(0, maxArticles);

  if (filtered.length === 0) return null;

  return (
    <section
      data-mana-component="category-section"
      data-category={category}
      style={{ marginBottom: 40 }}
    >
      {showHeader && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: `1px solid ${cfg.color}20`,
        }}>
          <span style={{ fontSize: 20 }}>{cfg.emoji}</span>
          <div>
            <h3 style={{
              fontFamily: "'Amiri', serif",
              fontSize: 22,
              color: cfg.color,
              fontWeight: 400,
              margin: 0,
              lineHeight: 1.2,
            }}>
              {cfg.label}
            </h3>
            <p style={{
              fontFamily: 'monospace',
              fontSize: 7,
              color: 'rgba(212,197,169,0.35)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              margin: '3px 0 0',
            }}>
              {filtered.length} article{filtered.length !== 1 ? 's' : ''} this issue
            </p>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((article) => (
          <MANAArticleCard key={article.id} article={article} variant="compact" showBridgeLinks={false} />
        ))}
      </div>
    </section>
  );
}

// ─── 4. MANA NEWSLETTER ISSUE ─────────────────────────────────────────────────
// Embeddable: <MANANewsletterIssueComponent issue={...} />
export function MANANewsletterIssueComponent({
  issue,
  layout = 'full',
}: {
  issue: MANANewsletterIssueData;
  layout?: 'full' | 'digest' | 'preview';
}) {
  const isPreview = layout === 'preview';
  const isDigest = layout === 'digest';

  return (
    <div
      data-mana-component="newsletter-issue"
      data-issue-id={issue.id}
      data-issue-number={issue.issue_number}
      style={{
        background: '#060d12',
        border: '1px solid rgba(201,168,76,0.15)',
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: "'Georgia', serif",
      }}
    >
      {/* Issue Header */}
      <div style={{
        padding: isPreview ? '16px 20px' : '28px 32px',
        background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, rgba(6,13,18,0) 100%)',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <p style={{
              fontFamily: 'monospace',
              fontSize: 8,
              letterSpacing: '.18em',
              textTransform: 'uppercase',
              color: 'rgba(201,168,76,0.5)',
              margin: '0 0 6px',
            }}>
              MANA Newsletter · Issue #{issue.issue_number}
            </p>
            <h2 style={{
              fontFamily: "'Amiri', serif",
              fontSize: isPreview ? 22 : 30,
              color: '#F5F0E8',
              fontWeight: 400,
              margin: '0 0 8px',
              lineHeight: 1.2,
            }}>
              {issue.title}
            </h2>
            <p style={{
              fontFamily: 'monospace',
              fontSize: 8,
              color: 'rgba(212,197,169,0.35)',
              letterSpacing: '.06em',
              margin: 0,
            }}>
              {new Date(issue.published_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {' · '}
              {issue.articles.length} articles
              {' · '}
              {issue.categories.length} categories
            </p>
          </div>
          {/* Category pills */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 200 }}>
            {issue.categories.map((cat) => (
              <MANACategoryBadge key={cat} category={cat} size="sm" showEmoji={false} />
            ))}
          </div>
        </div>
      </div>

      {/* Issue Content */}
      <div style={{ padding: isPreview ? '16px 20px' : '28px 32px' }}>
        {isPreview ? (
          // Preview: show first 3 articles
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {issue.articles.slice(0, 3).map((article) => (
              <MANAArticleCard key={article.id} article={article} variant="compact" showBridgeLinks={false} />
            ))}
            {issue.articles.length > 3 && (
              <p style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: 'rgba(212,197,169,0.35)',
                letterSpacing: '.08em',
                textAlign: 'center',
                margin: '8px 0 0',
              }}>
                +{issue.articles.length - 3} more articles
              </p>
            )}
          </div>
        ) : isDigest ? (
          // Digest: one article per category
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {issue.categories.map((cat) => {
              const catArticle = issue.articles.find(a => a.interest_category === cat);
              if (!catArticle) return null;
              return <MANAArticleCard key={cat} article={catArticle} variant="compact" showBridgeLinks={false} />;
            })}
          </div>
        ) : (
          // Full: all articles grouped by category
          <div>
            {Object.keys(CATEGORY_CONFIG).map((cat) => (
              <MANACategorySection
                key={cat}
                category={cat}
                articles={issue.articles}
                showHeader={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 5. MANA SOURCE STATUS BADGE ─────────────────────────────────────────────
// Embeddable: <MANASourceStatusBadge source="Sapelo Square" status="success" count={3} />
export function MANASourceStatusBadge({
  source,
  domain,
  status,
  articlesAdded = 0,
  type = 'news',
}: {
  source: string;
  domain: string;
  status: 'success' | 'error' | 'pending' | 'not_polled';
  articlesAdded?: number;
  type?: string;
}) {
  const typeColors: Record<string, string> = {
    partner: '#c9a84c',
    publication: '#8a8ab8',
    scholarly: '#2d8a87',
    news: '#2d7a47',
  };
  const color = typeColors[type] || '#c9a84c';
  const statusColor = status === 'success' ? '#2d7a47' : status === 'error' ? '#c47a7a' : 'rgba(212,197,169,0.3)';

  return (
    <div
      data-mana-component="source-status"
      data-source={domain}
      style={{
        padding: '10px 14px',
        background: '#0a1018',
        border: `1px solid ${color}15`,
        borderLeft: `3px solid ${color}40`,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 8, color: statusColor, letterSpacing: '.06em' }}>
        {status === 'success' ? '✓' : status === 'error' ? '✗' : '○'}
      </span>
      <div style={{ flex: 1 }}>
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#F5F0E8', margin: 0 }}>{source}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.04em', margin: '2px 0 0' }}>{domain}</p>
      </div>
      {articlesAdded > 0 && (
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.06em' }}>
          +{articlesAdded}
        </span>
      )}
      <span style={{
        fontFamily: 'monospace',
        fontSize: 6,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: color,
        padding: '2px 6px',
        border: `1px solid ${color}30`,
        borderRadius: 2,
        background: `${color}08`,
      }}>
        {type}
      </span>
    </div>
  );
}

// ─── 6. MANA EMBED GENERATOR ──────────────────────────────────────────────────
// Generates iframe embed code and JSON export for any MANA component
export function MANAEmbedGenerator({
  componentType,
  componentId,
  title,
  data,
}: {
  componentType: 'article-card' | 'category-section' | 'newsletter-issue' | 'source-grid';
  componentId?: string;
  title?: string;
  data?: unknown;
}) {
  const [activeTab, setActiveTab] = useState<'iframe' | 'json' | 'react'>('iframe');
  const { copied, copy } = useCopyToClipboard();

  const iframeUrl = `${EMBED_BASE_URL}/mrg-embed?component=${componentType}${componentId ? `&id=${componentId}` : ''}`;

  const iframeCode = `<iframe
  src="${iframeUrl}"
  width="100%"
  height="400"
  frameborder="0"
  style="border: none; border-radius: 6px;"
  title="${title || `MANA Newsletter — ${componentType}`}"
  loading="lazy"
></iframe>`;

  const reactCode = `import { ${componentType === 'article-card' ? 'MANAArticleCard' : componentType === 'category-section' ? 'MANACategorySection' : componentType === 'newsletter-issue' ? 'MANANewsletterIssueComponent' : 'MANASourceStatusBadge'} } from '@/app/mana-newsletter/components/MANANewsletterEmbeds';

// Usage:
// <${componentType === 'article-card' ? 'MANAArticleCard article={articleData}' : componentType === 'category-section' ? 'MANACategorySection category="marriage-family" articles={articles}' : componentType === 'newsletter-issue' ? 'MANANewsletterIssueComponent issue={issueData}' : 'MANASourceStatusBadge source="Sapelo Square" status="success"'} />`;

  const jsonExport = JSON.stringify(data || { componentType, componentId, embedUrl: iframeUrl }, null, 2);

  const codeMap = { iframe: iframeCode, json: jsonExport, react: reactCode };

  return (
    <div
      data-mana-component="embed-generator"
      style={{
        background: '#0a1018',
        border: '1px solid rgba(201,168,76,0.15)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', margin: 0 }}>
            ◈ Embed / Export
          </p>
          {title && (
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.6)', margin: '2px 0 0' }}>
              {title}
            </p>
          )}
        </div>
        <span style={{
          fontFamily: 'monospace',
          fontSize: 7,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: '#c9a84c',
          padding: '2px 8px',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 3,
          background: 'rgba(201,168,76,0.06)',
        }}>
          {componentType}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
        {(['iframe', 'react', 'json'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              fontFamily: 'monospace',
              fontSize: 8,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              padding: '8px 14px',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${activeTab === tab ? '#c9a84c' : 'transparent'}`,
              color: activeTab === tab ? '#c9a84c' : 'rgba(212,197,169,0.35)',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {tab === 'iframe' ? '⊞ iFrame' : tab === 'react' ? '⚛ React' : '{ } JSON'}
          </button>
        ))}
      </div>

      {/* Code block */}
      <div style={{ position: 'relative' }}>
        <pre style={{
          fontFamily: 'monospace',
          fontSize: 10,
          color: 'rgba(212,197,169,0.7)',
          background: 'rgba(0,0,0,0.3)',
          padding: '16px',
          margin: 0,
          overflowX: 'auto',
          lineHeight: 1.6,
          maxHeight: 200,
          overflowY: 'auto',
        }}>
          {codeMap[activeTab]}
        </pre>
        <button
          onClick={() => copy(codeMap[activeTab])}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontFamily: 'monospace',
            fontSize: 7,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            padding: '4px 10px',
            background: copied ? 'rgba(45,122,71,0.15)' : 'rgba(201,168,76,0.08)',
            border: `1px solid ${copied ? 'rgba(45,122,71,0.3)' : 'rgba(201,168,76,0.2)'}`,
            color: copied ? '#2d7a47' : '#c9a84c',
            borderRadius: 3,
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {copied ? '✓ Copied' : '⎘ Copy'}
        </button>
      </div>

      {/* Embed URL */}
      <div style={{
        padding: '10px 16px',
        borderTop: '1px solid rgba(201,168,76,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>
          Embed URL:
        </span>
        <a
          href={iframeUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: 'monospace',
            fontSize: 8,
            color: 'rgba(201,168,76,0.5)',
            textDecoration: 'none',
            letterSpacing: '.04em',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {iframeUrl}
        </a>
      </div>
    </div>
  );
}

// ─── 7. MANA NEWSLETTER EMBED PANEL ──────────────────────────────────────────
// Full panel showing all embeddable components with their embed codes
export default function MANANewsletterEmbeds() {
  const [selectedComponent, setSelectedComponent] = useState<string>('article-card');

  const COMPONENTS = [
    {
      id: 'article-card',
      label: 'Article Card',
      description: 'Individual article with category, title, summary, and bridge links',
      preview: (
        <MANAArticleCard
          article={{
            id: 'preview',
            source_name: 'Sapelo Square',
            source_domain: 'sapelo.org',
            article_title: 'The Legacy of African American Muslim Communities in the Industrial Midwest',
            article_url: 'https://sapelo.org',
            article_summary: 'An exploration of how African American Muslim communities in Pittsburgh, Detroit, and Chicago have maintained cultural and spiritual identity through industrial decline.',
            interest_category: 'history-heritage',
            relevance_score: 92,
            relevance_reason: 'Directly addresses MANA\'s core focus on African American Muslim heritage',
            tags: ['heritage', 'community', 'history', 'midwest'],
            status: 'approved',
            fetched_at: new Date().toISOString(),
          }}
        />
      ),
    },
    {
      id: 'category-badge',
      label: 'Category Badge',
      description: 'Compact category label for any MANA interest category',
      preview: (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.keys(CATEGORY_CONFIG).map((cat) => (
            <MANACategoryBadge key={cat} category={cat} />
          ))}
        </div>
      ),
    },
    {
      id: 'category-section',
      label: 'Category Section',
      description: 'Full category section with header and article list',
      preview: (
        <MANACategorySection
          category="justice-civic"
          articles={[
            {
              id: 'p1', source_name: 'MANA Justice', source_domain: 'manajustice.org',
              article_title: 'Community Organizing for Environmental Justice in River Rouge',
              article_url: 'https://manajustice.org', article_summary: 'MANA Justice partners with River Rouge residents to address industrial pollution.',
              interest_category: 'justice-civic', relevance_score: 95, tags: ['justice', 'environment'],
              status: 'approved', fetched_at: new Date().toISOString(),
            },
            {
              id: 'p2', source_name: 'CIOGC', source_domain: 'ciogc.org',
              article_title: 'Muslim Civic Engagement in Chicago\'s 2025 Municipal Elections',
              article_url: 'https://ciogc.org', article_summary: 'CIOGC mobilizes Muslim voters across Chicago\'s South and West Sides.',
              interest_category: 'justice-civic', relevance_score: 88, tags: ['civic', 'voting', 'chicago'],
              status: 'approved', fetched_at: new Date().toISOString(),
            },
          ]}
        />
      ),
    },
    {
      id: 'source-status',
      label: 'Source Status',
      description: 'Individual source status badge showing poll results',
      preview: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <MANASourceStatusBadge source="Sapelo Square" domain="sapelo.org" status="success" articlesAdded={3} type="publication" />
          <MANASourceStatusBadge source="MANA Justice" domain="manajustice.org" status="success" articlesAdded={5} type="partner" />
          <MANASourceStatusBadge source="Yaqeen Institute" domain="yaqeeninstitute.org" status="error" type="scholarly" />
        </div>
      ),
    },
  ];

  const selected = COMPONENTS.find(c => c.id === selectedComponent);

  return (
    <div style={{ background: '#060d12', padding: '48px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>
            ◈ MANA Newsletter · Embeddable Components
          </p>
          <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#F5F0E8', fontWeight: 400, marginBottom: 8, lineHeight: 1.2 }}>
            Export & Embed
          </h2>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, maxWidth: 600 }}>
            Each MANA newsletter component is individually exportable as an iFrame embed, React component, or JSON data. Use these to embed MANA content in partner websites, community platforms, and external publications.
          </p>
        </div>

        {/* Component selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
          {COMPONENTS.map((comp) => (
            <button
              key={comp.id}
              onClick={() => setSelectedComponent(comp.id)}
              style={{
                fontFamily: 'monospace',
                fontSize: 8,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                padding: '7px 14px',
                background: selectedComponent === comp.id ? 'rgba(201,168,76,0.1)' : 'transparent',
                border: `1px solid ${selectedComponent === comp.id ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
                color: selectedComponent === comp.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {comp.label}
            </button>
          ))}
        </div>

        {selected && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Preview */}
            <div>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 12 }}>
                ◈ Preview
              </p>
              <div style={{
                background: '#0a1018',
                border: '1px solid rgba(201,168,76,0.1)',
                borderRadius: 6,
                padding: 20,
              }}>
                {selected.preview}
              </div>
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6, marginTop: 10 }}>
                {selected.description}
              </p>
            </div>

            {/* Embed Generator */}
            <div>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 12 }}>
                ◈ Embed Code
              </p>
              <MANAEmbedGenerator
                componentType={selected.id as 'article-card' | 'category-section' | 'newsletter-issue' | 'source-grid'}
                componentId={selected.id}
                title={selected.label}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
